import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaCalendarAlt, FaDollarSign, FaEye, FaBuilding, FaPaypal, FaUser, FaEnvelope, FaPhone, FaGlobe, FaChevronRight } from 'react-icons/fa';

const QuotationRequestCard = ({ request, onViewQuotation, onPayWithPayPal }) => {
  const navigate = useNavigate();

  const getRequestStatusColor = (quotations) => {
    if (!quotations || quotations.length === 0) return 'bg-gray-400';
    if (quotations.some(q => q.status === 'Accepted')) return 'bg-green-600';
    if (quotations.every(q => q.status === 'Ignored')) return 'bg-gray-400';
    return 'bg-yellow-500';
  };

  const getQuotationStatusColor = (status) => {
    switch (status) {
      case 'Accepted': return 'text-green-600';
      case 'Ignored': return 'text-gray-500';
      default: return 'text-yellow-500';
    }
  };

  const CompanyLogo = ({ logo, companyName }) => {
    if (logo) {
      return (
        <div className="w-16 h-16 mr-3 flex-shrink-0 overflow-hidden bg-gray-100 rounded-full">
          <img 
            src={process.env.REACT_APP_IMAGE_BASE_URL + logo} 
            alt={`${companyName} logo`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-full h-full items-center justify-center bg-gray-200 hidden rounded-full">
            <FaBuilding className="text-gray-400 w-8 h-8" />
          </div>
        </div>
      );
    } else {
      return (
        <div className="w-16 h-16 mr-3 flex-shrink-0 overflow-hidden bg-gray-200 flex items-center justify-center rounded-full">
          <FaBuilding className="text-gray-400 w-8 h-8" />
        </div>
      );
    }
  };

  if (!request) {
    return <div>No request data available</div>;
  }

  const { quotationCompany } = request;

  const handleShowDetails = () => {
    navigate(`/request/${request.quotationCompany.id}`);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
      <div className={`${getRequestStatusColor(request.quotations)} text-white py-2 px-4 flex justify-between items-center`}>
        <h3 className="text-lg font-semibold">
          {quotationCompany.name}
          {quotationCompany.business && ` - ${quotationCompany.business}`}
        </h3>
        <button
          onClick={handleShowDetails}
          className="text-white hover:text-gray-200 flex items-center focus:outline-none"
        >
          <span className="mr-1">Details</span>
          <FaChevronRight />
        </button>
      </div>
      <div className="p-4">
        <div className="flex items-start mb-4">
          <CompanyLogo logo={quotationCompany.logo} companyName={quotationCompany.name} />
          <div className="flex-grow">
            <h4 className="font-semibold text-lg text-gray-800">{quotationCompany.name}</h4>
            {quotationCompany.code && <p className="text-sm text-gray-600">{quotationCompany.code}</p>}
            {quotationCompany.address && (
              <p className="text-sm text-gray-600 flex items-center mt-1">
                <FaMapMarkerAlt className="mr-2 text-green-600 flex-shrink-0" />
                <span className="truncate">{quotationCompany.address}</span>
              </p>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            {quotationCompany.contactPerson && (
              <p className="text-sm text-gray-600 flex items-center">
                <FaUser className="mr-2 text-blue-600 flex-shrink-0" />
                <span>{quotationCompany.contactPerson}</span>
              </p>
            )}
            {quotationCompany.email && (
              <p className="text-sm text-gray-600 flex items-center mt-1">
                <FaEnvelope className="mr-2 text-blue-600 flex-shrink-0" />
                <a href={`mailto:${quotationCompany.email}`} className="hover:underline">{quotationCompany.email}</a>
              </p>
            )}
          </div>
          <div>
            {quotationCompany.callPhone && (
              <p className="text-sm text-gray-600 flex items-center">
                <FaPhone className="mr-2 text-blue-600 flex-shrink-0" />
                <a href={`tel:${quotationCompany.callPhone}`} className="hover:underline">{quotationCompany.callPhone}</a>
              </p>
            )}
            {quotationCompany.smsPhone && (
              <p className="text-sm text-gray-600 flex items-center mt-1">
                <FaPhone className="mr-2 text-blue-600 flex-shrink-0" />
                <span>{quotationCompany.smsPhone} (SMS)</span>
              </p>
            )}
          </div>
        </div>
        
        {quotationCompany.webSite && (
          <p className="text-sm text-gray-600 flex items-center mb-4">
            <FaGlobe className="mr-2 text-blue-600 flex-shrink-0" />
            <a href={quotationCompany.webSite} target="_blank" rel="noopener noreferrer" className="hover:underline">{quotationCompany.webSite}</a>
          </p>
        )}

        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-semibold mb-2 text-gray-800">Request Details:</h4>
          {(request.rentalStartDate && request.rentalEndDate) && (
            <p className="text-gray-600 mb-2 flex items-center text-sm">
              <FaCalendarAlt className="mr-2 text-green-600 flex-shrink-0" />
              <span>{new Date(request.rentalStartDate).toLocaleDateString()} - {new Date(request.rentalEndDate).toLocaleDateString()}</span>
            </p>
          )}
          {(request.customerAddress || request.addressName) && (
            <p className="text-gray-600 mb-2 flex items-center text-sm">
              <FaMapMarkerAlt className="mr-2 text-green-600 flex-shrink-0" />
              <span>
                {request.customerAddress}
                {request.addressName && ` (${request.addressName})`}
              </span>
            </p>
          )}
        </div>

        <div className="border-t border-gray-200 pt-4 mt-4">
          <h4 className="font-semibold mb-2 text-gray-800">Quotations:</h4>
          {request.quotations && request.quotations.length > 0 ? (
            <ul className="space-y-4">
              {request.quotations.map((quotation, index) => (
                <li key={index} className="bg-gray-50 p-3 rounded flex">
                  <CompanyLogo logo={quotation.byCompanyLogo} companyName={quotation.byCompanyName} />
                  <div className="flex-grow flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="flex items-center font-semibold text-lg mr-2">
                          <FaDollarSign className="text-green-600 mr-1" />
                          {quotation.amount.toFixed(2)}
                        </span>
                        <span className="text-sm text-gray-600 truncate">{quotation.byCompanyName}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between">
                      <span className={`${getQuotationStatusColor(quotation.status)} font-semibold text-sm mr-2`}>
                        {quotation.status}
                      </span>
                      <div className="flex space-x-2 mt-2 sm:mt-0">
                        <button 
                          onClick={() => onViewQuotation(quotation.quotationNumber)}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-full text-sm shadow-lg transform transition hover:scale-105 flex items-center"
                        >
                          <FaEye className="mr-1" /> View
                        </button>
                        {quotation.status === 'Accepted' && (
                          <button 
                            onClick={() => {
                              if (typeof onPayWithPayPal === 'function') {
                                onPayWithPayPal(quotation);
                              } else {
                                console.warn('onPayWithPayPal is not a function');
                              }
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-1 px-3 rounded-full text-sm shadow-lg transform transition hover:scale-105 flex items-center whitespace-nowrap"
                          >
                            <FaPaypal className="mr-1" /> Pay
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 italic">No quotations received yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationRequestCard;